import React from 'react';
import { connect } from 'react-redux';
import last from 'lodash/last';
import map from 'lodash/map';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import autoBind from 'auto-bind';
import Tooltip from 'rc-tooltip';
import { Transition } from 'react-transition-group';
import { Header, PopupTextBlock } from '../Popups.styles';
import { ErrorPopup } from '../ErrorPopup/ErrorPopup';
import { extractSelectedTicketFromState, getSmbOfSelectedTicket, getPartnerTagsArray } from '../../../extractFromState';
import { createPopup, closePopup } from '../../../services/popupService';
import { ConfirmButton, DismissButton } from '../Popups.styles';
import { DropdownSelect } from '../../common/formElements';
import { SALESFORCE } from '../../../constants/conversationTypes';
import { showError, showErrorMessage } from 'util/errorPopupUtil';
import { ErrorStrings } from '../../../constants/fixedMessages';
import { updatePartnerTags } from '../../../api/partnerTags';
import {
  Bar,
  ContentWrapper,
  DropdownArea,
  Flag,
  FlagContainer,
  FlagText,
  Image,
  PopupContent,
  ProgressBar,
  ProgressSection,
  ProgressText,
  SectionTitle,
  StepContainer,
  StepLeft,
  StepRight,
  ThankYouSection,
  TooltipText,
} from './PartnerTaggingPopup.styles';
import * as ErrorService from 'services/ErrorService/ErrorService';
import { handleError } from 'services/ErrorService/ErrorService';

export class PartnerTaggingPopupComponent extends React.Component {
  state = {
    currentSectionStep: 0, // index of which step we are on
    smbValueArray: [], // values in smbCategory drop down
    completedSmbTags: [], // smb values set after user presses done or next
    selectedFormId: null,
    isFormSelected: false,
    partnerFormsArray: [],
    thankYouPageActive: false, // determines if thank you page displays
    messageSendInProgress: false,
    currentChildConditionalFieldsLength: 0,
    partnerTagsArrayLength: 0,
    partnerTags: [],
  };

  constructor(props) {
    super(props);
    autoBind(this);
  }

  get smbCategoryData() {
    return this.state.partnerTags[this.state.currentSectionStep];
  }

  get smbActiveCategoryList() {
    const { smbValueArray } = this.state;
    // find all of the smb category nodes we want to display
    let activeList = [this.smbCategoryData];
    // traverse down the tree along the edges of already selected values
    for (let edge of smbValueArray) {
      // find the children of the last node we added
      const children = last(activeList).children;
      // add the child at the edge
      if (edge in children) {
        activeList.push(children[edge]);
      }
    }

    return activeList;
  }

  get lastSmbCategorySet() {
    const smbActiveCategoryList = this.smbActiveCategoryList;

    return last(smbActiveCategoryList);
  }

  get percentageOfSectionsComplete() {
    return ((this.state.currentSectionStep + 1) / this.state.partnerTagsArrayLength) * 100;
  }

  get sectionNumberText() {
    return `Set ${this.state.currentSectionStep + 1} of ${this.state.partnerTagsArrayLength}`;
  }

  get toolTipText() {
    return `These tags are for ${this.props.partnerName}'s ticketing system. This is a seperate system
    from Simplr's internal tags. ${this.props.partnerName} counts on us to categorize incoming messages so they can
    effectively track valuable partner metrics.`;
  }

  componentDidMount() {
    this.hydratePartnerForms();
    this.hydratePartnerTags();
  }

  componentDidUpdate() {
    /**
     * We need to set the progress bar length depending on the presence
     * of child conditional fields. We also need to be careful and set the progress bar
     * length back to it's original size if there are no child conditional fields on the current
     * option that the user selects.
     *
     */

    let { partnerTagsArrayLength, currentChildConditionalFieldsLength } = this.state;

    const lastSmbCategorySetValue = get(this.lastSmbCategorySet, 'value');
    const childConditionalFieldsLength = get(this.getConditionalFieldsIfPresent(lastSmbCategorySetValue), 'length', 0);

    const progressBarWithChildFieldsLength = this.state.partnerTags.length + childConditionalFieldsLength;
    const isProgressBarLongerThanCategoryArray = partnerTagsArrayLength > this.state.partnerTags.length;

    if (partnerTagsArrayLength !== progressBarWithChildFieldsLength) {
      this.setState({
        partnerTagsArrayLength:
          this.state.partnerTags.length + childConditionalFieldsLength - currentChildConditionalFieldsLength,
        currentChildConditionalFieldsLength: childConditionalFieldsLength,
      });
    } else if (isProgressBarLongerThanCategoryArray && childConditionalFieldsLength === 0) {
      this.setState({
        partnerTagsArrayLength: this.state.partnerTags.length,
        currentChildConditionalFieldsLength: 0,
      });
    }
  }

  hydratePartnerForms() {
    const partnerForms = this.props.partnerTagsArray.reduce((acc, partnerTag) => {
      if (partnerTag.form) {
        acc.push(partnerTag.form);
      }

      return acc;
    }, []);

    if (!isEmpty(partnerForms)) {
      this.setState({ partnerFormsArray: partnerForms });
    }
  }

  hydratePartnerTags() {
    if (isEmpty(this.state.partnerFormsArray)) {
      const partnerTags = get(head(this.props.partnerTagsArray), 'tags', []);
      this.setState({
        partnerTags: partnerTags,
        partnerTagsArrayLength: partnerTags.length,
      });
    }
  }

  handleSMBCategoryFormSelection(formId) {
    if (!formId) {
      return;
    }

    const tagsInSelectedForm = get(
      this.props.partnerTagsArray.find((item) => item.form.id.toString() === formId),
      'tags',
      []
    );
    this.setState({
      selectedFormId: formId,
      partnerTags: tagsInSelectedForm,
      partnerTagsArrayLength: tagsInSelectedForm.length,
    });
  }

  handleSMBCategorySelection(e, treeDepth) {
    const { value } = e.target;
    // TODO: Might make sense to clear downstream tags if selecting the default option
    if (!value) {
      return; // Prevent reselecting default option
    }

    let { smbValueArray } = this.state;

    // see if re-picking value already set
    if (treeDepth < smbValueArray.length) {
      // new value at previously selected dropdown
      if (smbValueArray[treeDepth] !== value) {
        // trim off any values that come at this depth or deepr
        smbValueArray = smbValueArray.slice(0, treeDepth);
        // add the new value in
        smbValueArray.push(value);
      }
    } else {
      smbValueArray.push(value);
    }

    this.setState({ smbValueArray });
  }

  renderSMBSection() {
    return this.smbActiveCategoryList.map(this.renderOneCategoryDropDown);
  }

  getValueAtDepth(treeDepth) {
    const { smbValueArray } = this.state;

    return smbValueArray[treeDepth];
  }

  handleNextOnForm() {
    this.setState({ isFormSelected: true });
  }

  handleNext(params) {
    const { isLastStep } = params;
    let { completedSmbTags, currentSectionStep } = this.state;

    if (this.props.ticket.ticketType === SALESFORCE) {
      completedSmbTags.push(this.smbActiveCategoryList.slice(1));
    } else {
      const tag = {
        id: this.smbCategoryData.id,
        value: this.lastSmbCategorySet.value,
      };

      if (this.lastSmbCategorySet.path) {
        tag.path = this.lastSmbCategorySet.path;
      }
      completedSmbTags.push(tag);
    }

    const childConditionalFields = this.getConditionalFieldsIfPresent(this.lastSmbCategorySet.value);

    if (childConditionalFields && childConditionalFields.length > 0) {
      let updatedSmbTags = Object.assign([], this.state.partnerTags);

      updatedSmbTags.splice(currentSectionStep + 1, 0, ...childConditionalFields);

      this.setState({ partnerTags: updatedSmbTags });
    }

    currentSectionStep += 1;

    // On the last step if a conditional field is present then we don't want to submit the tags yet
    if (isLastStep && childConditionalFields && childConditionalFields.length > 0) {
      this.setState({
        completedSmbTags,
        smbValueArray: [],
        currentSectionStep: currentSectionStep + childConditionalFields.length - 1,
      });
    } else if (isLastStep) {
      this.onSubmit();
    } else {
      this.setState({ completedSmbTags, smbValueArray: [], currentSectionStep });
    }

    this.setState({ currentChildConditionalFieldsLength: 0 });
  }

  getConditionalFieldsIfPresent() {
    return this.smbActiveCategoryList.reduce((acc, category) => {
      if (category.extraTags) {
        category.extraTags.forEach((extraTag) => {
          acc.push(extraTag);
        });
      }

      return acc;
    }, []);
  }

  isTaggingFinished() {
    // see if the index of our selection is on the last item
    return this.state.currentSectionStep + 1 === this.state.partnerTagsArrayLength;
  }

  isSectionComplete() {
    // if the last choice doesn't have children, then we can proceed
    return isEmpty(this.lastSmbCategorySet) || isEmpty(this.lastSmbCategorySet.children);
  }

  shouldRenderFormSelection() {
    // if we should set form then show form selection
    return get(this.state.partnerFormsArray, 'length', 0) > 0 && !this.state.isFormSelected;
  }

  onSubmit = async () => {
    try {
      this.setState({ thankYouPageActive: true, messageSendInProgress: true });
      await this.updateTicketFieldsFromSmbCategories();
      this.props.submitMessage();
      setTimeout(() => {
        closePopup();
      }, 200);
    } catch (error) {
      handleError({ error });
      this.props.showError(error);
    } finally {
      this.setState({ messageSendInProgress: false });
    }
  };

  async updateTicketFieldsFromSmbCategories() {
    const { ticket } = this.props;
    if (!ticket) {
      showErrorMessage(new Error(ErrorStrings.SELECTED_TICKET_NOT_FOUND));

      return;
    }

    const { completedSmbTags, selectedFormId } = this.state;
    if (!isEmpty(completedSmbTags)) {
      await updatePartnerTags(ticket, completedSmbTags, selectedFormId);
    }
  }

  renderFormButton() {
    if (this.shouldRenderFormSelection()) {
      return (
        <ConfirmButton onClick={() => this.handleNextOnForm()} disabled={!this.state.selectedFormId}>
          NEXT
        </ConfirmButton>
      );
    } else if (this.isTaggingFinished()) {
      return (
        <ConfirmButton
          onClick={() => this.handleNext({ isLastStep: true })}
          disabled={!this.isSectionComplete() || this.state.thankYouPageActive}
          locked={this.state.thankYouPageActive}
        >
          DONE
        </ConfirmButton>
      );
    }

    return (
      <ConfirmButton onClick={() => this.handleNext({ isLastStep: false })} disabled={!this.isSectionComplete()}>
        NEXT
      </ConfirmButton>
    );
  }

  renderFormCategoryDropDown(forms) {
    const options = map(forms, (form) => {
      return (
        form?.name && (
          <option key={form.id} value={form.id}>
            {form.name}
          </option>
        )
      );
    });

    return (
      <StepContainer>
        <StepLeft>Step 1</StepLeft>
        <StepRight>
          <DropdownSelect
            value={this.state.selectedFormId || ''}
            onChange={({ target: { value } }) => this.handleSMBCategoryFormSelection(value)}
          >
            <option key={'default'} value={''}>
              Select
            </option>
            {options}
          </DropdownSelect>
        </StepRight>
      </StepContainer>
    );
  }

  renderOneCategoryDropDown(category, treeDepth) {
    // if the category has no children, don't render the dropdown
    if (isEmpty(category) || isEmpty(category.children)) {
      return;
    }
    // /TODO: Create deterministic ordering for object iteration
    const options = map(category.children, (child, edge) => {
      return (
        child?.name && (
          <option key={child.value} value={edge}>
            {child.name}
          </option>
        )
      );
    });

    return (
      <StepContainer key={treeDepth}>
        <StepLeft>Step {treeDepth + 1}</StepLeft>
        <StepRight>
          <DropdownSelect
            ticketFieldId={this.smbCategoryData.id.toString()}
            key={`${category.value}_${treeDepth}`}
            value={this.getValueAtDepth(treeDepth)}
            onChange={(e) => this.handleSMBCategorySelection(e, treeDepth)}
          >
            <option key="default" value="">
              Select
            </option>
            {options}
          </DropdownSelect>
        </StepRight>
      </StepContainer>
    );
  }

  render() {
    // used in animations
    const transitionStyles = {
      entering: { opacity: 0 },
      entered: { opacity: 1 },
      exiting: { opacity: 0 },
      exited: { opacity: 0 },
    };

    return (
      <div>
        <ContentWrapper>
          {this.state.thankYouPageActive ? (
            <ThankYouSection>
              <Transition in={true} timeout={100} appear={true}>
                {(state) => (
                  <FlagContainer
                    style={{
                      ...transitionStyles[state],
                    }}
                  >
                    <Flag alt="Flag" src={'/images/flag.svg'} />
                    <FlagText>Thank you for helping us improve our system.</FlagText>
                  </FlagContainer>
                )}
              </Transition>
            </ThankYouSection>
          ) : (
            <PopupContent>
              <PopupTextBlock>
                <Image src="/images/green-alert-bell.svg" alt="green-alert-bell" />
                <Header>
                  Please tag this ticket for <br />
                  the partner's record.
                </Header>
                <Tooltip
                  placement="bottom"
                  overlay={this.toolTipText}
                  overlayStyle={{ width: '300px' }}
                  width={'200px'}
                  arrowContent={<div className="rc-tooltip-arrow-inner" />}
                >
                  <TooltipText>Why do I have to do tagging again?</TooltipText>
                </Tooltip>
              </PopupTextBlock>

              <DropdownArea>
                {this.shouldRenderFormSelection() ? (
                  <>
                    <SectionTitle>Please select form first</SectionTitle>
                    {this.renderFormCategoryDropDown(this.state.partnerFormsArray)}
                  </>
                ) : (
                  <>
                    <SectionTitle>{get(this.smbCategoryData, 'title', '')}</SectionTitle>
                    {this.renderSMBSection()}
                  </>
                )}
              </DropdownArea>

              <ProgressSection>
                {(this.state.isFormSelected || get(this.state.partnerFormsArray, 'length', 0) === 0) && (
                  <>
                    <ProgressText>{this.sectionNumberText}</ProgressText>
                    <ProgressBar>
                      <Bar width={this.percentageOfSectionsComplete} />
                    </ProgressBar>
                  </>
                )}
              </ProgressSection>
            </PopupContent>
          )}
        </ContentWrapper>
        <DismissButton
          onClick={() => {
            // do not allow closing if the message is still sending
            if (this.state.messageSendInProgress) {
              return;
            }
            closePopup();
            this.props.closeCallback();
          }}
        >
          CLOSE
        </DismissButton>
        {this.renderFormButton()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  partnerTagsArray: getPartnerTagsArray(state),
  ticket: extractSelectedTicketFromState(state),
  partnerName: getSmbOfSelectedTicket(state),
});

const PartnerTaggingPopupContainer = connect(mapStateToProps, {
  showError,
})(PartnerTaggingPopupComponent);

export default function PartnerTaggingPopup(props) {
  return (
    <ErrorService.ErrorBoundary
      customFields={{ system: 'Partner Tagging Popup' }}
      fallback={
        <ErrorPopup message="An error is preventing this ticket from being tagged. Please let us know if the problem persists." />
      }
    >
      <PartnerTaggingPopupContainer {...props} />
    </ErrorService.ErrorBoundary>
  );
}

export const openPartnerTaggingPopup = (submitMessage, closeCallback) =>
  createPopup({
    type: 'EscalatePopupComponent',
    title: null,
    content: <PartnerTaggingPopup submitMessage={submitMessage} closeCallback={closeCallback} />,
    noOverlay: false,
    closeOnOutsideClick: false,
  });
